import { useState } from 'react'
import { Modal, PlusIcon } from './modal.jsx'
import {
  CLOUDS,
  InvalidCloudAccountError,
  isCloud,
  isRole,
  roleHasCapability,
  validateRegistration,
} from '@qim/shared'
import { ApiError, fetchBootstrapTemplate, registerAccount } from '../api.js'
import { AccountLink } from './account-link.jsx'
import { TemplateButton } from './account.jsx'
import type { RegisteredAccountResult } from '../api.js'
import type { Cloud, CloudAccountRegistration } from '@qim/shared'
import type { FormEvent, JSX, ReactNode } from 'react'

/**
 * Registering a cloud account, and handing over what it mints.
 *
 * ## The cloud comes first (IM-44)
 *
 * Each cloud's account is a different shape — twelve digits on AWS, a
 * subscription and its tenant on Azure — so the form asks which cloud before
 * it shows any field, and then shows only that branch's. Picking for the
 * person would be the prototype's bug in a form: a value read as whatever
 * cloud the default said.
 *
 * On Azure registering mints nothing to copy. Lighthouse has no external id;
 * the delegation template carries a mark minted for this registration, so the
 * result offers the template rather than a value, and says nothing is lost by
 * leaving. Which scope is delegated is the customer's statement, with no
 * default: Reader on a shared subscription reads every Log Analytics workspace
 * in it.
 *
 * ## Why this screen matters more than it looks
 *
 * This is the one write in the product that cannot be repeated. The API mints
 * the external ID here, returns it exactly once, and there is deliberately no
 * read path afterwards — the listing projects columns that exclude the
 * ciphertext, and recovering one is unbuilt on purpose because it should be its
 * own audited action.
 *
 * So this component has exactly one chance to put that value in front of a
 * person, and it has to *say* that rather than looking like something they can
 * come back for. It was previously a `curl`, where the value scrolled past in a
 * terminal — the worst available place for it.
 *
 * ## Why the form calls the API's own validator
 *
 * `validateRegistration` is the function the handler uses. Calling it here means
 * the form refuses exactly what the API refuses, with the same message, before
 * making a request — rather than a second set of rules that can drift. It lives
 * in `shared` precisely so both sides can reach it.
 */
export const RegisterAccount = ({
  baseUrl,
  tenantId,
  role,
  onRegistered,
  onOpen,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onRegistered: () => void
  /** Navigates in place, to the new account's onboarding screen (IM-181). */
  readonly onOpen: (path: string) => void
}): JSX.Element | null => {
  const [cloud, setCloud] = useState<Cloud | undefined>(undefined)
  // Every cloud's fields by name; only the picked cloud's are ever sent.
  const [values, setValues] = useState<Readonly<Record<string, string>>>({})
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [result, setResult] = useState<RegisteredAccountResult | undefined>(undefined)
  const [open, setOpen] = useState(false)

  if (!isRole(role) || !roleHasCapability(role, 'account:onboard')) return null

  const form = cloud === undefined ? undefined : REGISTRATION[cloud]
  const setField = (name: string, value: string): void =>
    setValues((current) => ({ ...current, [name]: value }))

  /** What the form would send, branch by branch — never another cloud's fields. */
  const draft = (): Record<string, unknown> | undefined =>
    form === undefined
      ? undefined
      : {
          cloud,
          ...Object.fromEntries(
            form.fields.map((field) => [
              field.name,
              values[field.name] ?? (field.kind === 'text' ? '' : undefined),
            ]),
          ),
          label,
        }

  const touched =
    label !== '' ||
    (form?.fields.some((field) => field.kind === 'text' && (values[field.name] ?? '') !== '') ??
      false)

  /** The API's own rules, run here so a refusal costs no round trip. */
  const validate = (
    body: Record<string, unknown> | undefined,
  ): { registration?: CloudAccountRegistration; problem?: string } => {
    if (body === undefined) return {}
    try {
      return { registration: validateRegistration(body) }
    } catch (error: unknown) {
      return {
        problem:
          error instanceof InvalidCloudAccountError
            ? error.message
            : 'Those details are not valid.',
      }
    }
  }
  const validated = validate(draft())

  /*
   * Shown only once something has been typed: a fresh form full of refusals is
   * noise. An unpicked choice (Azure's delegation scope) is said by the radios
   * themselves, so while one is unpicked the other fields are checked as
   * though its first option were picked — a GUID typed wrong still says so —
   * and the button simply stays disabled.
   */
  const unpicked =
    form?.fields.flatMap((field) =>
      field.kind === 'choice' && values[field.name] === undefined
        ? [[field.name, field.options[0]?.value] as const]
        : [],
    ) ?? []
  const problem = !touched
    ? undefined
    : unpicked.length > 0
      ? validate({ ...draft(), ...Object.fromEntries(unpicked) }).problem
      : validated.problem
  const ready = validated.registration !== undefined

  const reset = (): void => {
    setCloud(undefined)
    setValues({})
    setLabel('')
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    const registration = validated.registration
    if (registration === undefined) return
    setBusy(true)
    setFailure(undefined)
    try {
      const registered = await registerAccount(baseUrl, tenantId, registration)
      setOpen(false)
      setResult(registered)
      reset()
      /*
       * Deliberately not `onRegistered()` here.
       *
       * That reloads the customer screen, and until IM-88 the reload blanked it
       * to `loading` — unmounting this component and destroying `result` before
       * the panel below could render. The external ID is minted once and has no
       * read path, so the reload silently threw away the only copy of the value
       * the customer needs, leaving an account nobody can finish onboarding.
       *
       * `App` no longer unmounts on a re-read, which is the actual fix. This is
       * the second half: the list refreshes when the panel is dismissed, so
       * there is never a reload in flight while the irreplaceable value is on
       * screen. A few seconds of a stale account list is not a comparable cost.
       */
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 409
          ? // Specifically, not generically: an account already registered is a
            // different situation from a malformed request, and the person is
            // probably looking at the account they already have.
            `That ${form?.accountNoun ?? 'account'} is already registered for this customer. It is in the list above.`
          : error instanceof ApiError && error.status === 400
            ? 'The control plane refused those details.'
            : 'Could not register the account just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (result !== undefined) {
    const done = (): void => {
      setResult(undefined)
      onRegistered()
    }
    const cloud = result.account.cloud
    // An account key always names a cloud this build knows; a response that
    // did not is shown as registered and nothing more, never as another
    // cloud's next steps.
    if (!isCloud(cloud)) {
      return (
        <section className="panel">
          <h3>Registered {result.account.label}</h3>
          <div className="actions">
            <button type="button" className="primary" onClick={done}>
              Done
            </button>
          </div>
        </section>
      )
    }
    const { Registered } = REGISTRATION[cloud]
    return (
      <Registered
        result={result}
        baseUrl={baseUrl}
        tenantId={tenantId}
        role={role}
        onOpen={onOpen}
        done={done}
      />
    )
  }

  if (!open) {
    return (
      <div className="panel-action">
        <button type="button" className="primary" onClick={() => setOpen(true)}>
          <PlusIcon />
          Register account
        </button>
      </div>
    )
  }

  return (
    /*
     * The *form* is a modal; the result deliberately is not (IM-146).
     *
     * What comes back is the external ID, which is shown exactly once and
     * cannot be retrieved afterwards by anything. A modal can be dismissed by
     * Esc or a click on the backdrop, and losing that value to a stray click
     * means registering the account again — so the result stays a panel with an
     * explicit Done.
     */
    <Modal title="Register a cloud account" onCancel={() => setOpen(false)}>
      <form onSubmit={(event) => void submit(event)}>
        <fieldset className="field kind-choice">
          <legend>Which cloud?</legend>
          {CLOUDS.map((each) => (
            <label key={each} htmlFor={REGISTRATION[each].radioId}>
              <input
                id={REGISTRATION[each].radioId}
                type="radio"
                name="cloud"
                checked={cloud === each}
                onChange={() => setCloud(each)}
              />{' '}
              {REGISTRATION[each].name}
            </label>
          ))}
        </fieldset>
        {form?.fields.map((field) => (
          <Field
            key={field.name}
            field={field}
            value={values[field.name]}
            onChange={(value) => setField(field.name, value)}
          />
        ))}
        {cloud === undefined ? null : (
          <div className="field">
            <label htmlFor="accountLabel">Label</label>
            <input
              id="accountLabel"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              aria-describedby="accountLabel-hint"
            />
            <span id="accountLabel-hint" className="hint">
              What this account is for &mdash; &ldquo;Trial&rdquo;, &ldquo;Production&rdquo;.
            </span>
          </div>
        )}
        {problem === undefined ? null : <p className="failure">{problem}</p>}
        {form === undefined ? null : (
          <p role="note" className="muted">
            {form.note}
          </p>
        )}
        <div className="actions">
          <button type="button" className="ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !ready}>
            {busy ? 'Registering…' : 'Register account'}
          </button>
        </div>
        {failure === undefined ? null : <p className="failure">{failure}</p>}
      </form>
    </Modal>
  )
}

/** One field of a cloud's account, as the form asks for it. */
type FieldSpec =
  | {
      readonly kind: 'text'
      readonly name: string
      readonly label: string
      readonly hint: ReactNode
      readonly inputMode?: 'numeric'
    }
  | {
      readonly kind: 'choice'
      readonly name: string
      readonly legend: string
      readonly hint: ReactNode
      readonly options: readonly {
        readonly value: string
        readonly id: string
        readonly label: ReactNode
      }[]
    }

interface RegisteredProps {
  readonly result: RegisteredAccountResult
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onOpen: (path: string) => void
  /** Dismisses the result, which is what refreshes the list — see `submit`. */
  readonly done: () => void
}

/**
 * What registering asks for, and hands back, per cloud (IM-48).
 *
 * The form is otherwise one form: it picks a cloud, renders that cloud's
 * fields, and sends only them. Keyed by `Cloud`, so a cloud added to `CLOUDS`
 * stops this compiling until it says what its account is and what
 * registering it leaves the person holding — the console's half of the
 * intended alarm. It used to compare `cloud` to a name in eleven places, and a
 * third cloud would have rendered no fields and been told it had registered
 * "an AWS account".
 */
const REGISTRATION: {
  readonly [C in Cloud]: {
    /** The radio's label. */
    readonly name: string
    readonly radioId: string
    /** What a 409 calls an account of this cloud. */
    readonly accountNoun: string
    readonly fields: readonly FieldSpec[]
    /** Said before registering: whether anything is shown only once. */
    readonly note: ReactNode
    /** The result, which differs by what registering mints. */
    readonly Registered: (props: RegisteredProps) => JSX.Element
  }
} = {
  aws: {
    name: 'AWS',
    radioId: 'cloudAws',
    accountNoun: 'AWS account',
    fields: [
      {
        kind: 'text',
        name: 'awsAccountId',
        label: 'AWS account id',
        hint: 'Exactly 12 digits.',
        inputMode: 'numeric',
      },
    ],
    note: (
      <>
        Registering mints an external ID that is shown <strong>once</strong>, on the next screen.
        Have somewhere to put it before you continue.
      </>
    ),
    Registered: ({ result, tenantId, role, onOpen, done }) => (
      <section className="panel">
        <h3>Registered {result.account.label}</h3>
        {/* role="alert" deliberately: this is the only time this value is ever
          shown, so it should be announced rather than merely rendered. */}
        <p role="alert">
          <strong>Copy the external ID now.</strong> It is shown once and cannot be retrieved again
          — not by this console, not by the API, not from the database. If it is lost, the account
          has to be registered again.
        </p>
        <dl className="detail">
          <dt>External ID</dt>
          <dd>
            <code>{result.externalId}</code>
          </dd>
          <dt>Qrvey principal</dt>
          {/* What the customer's trust policy will name. Read-only by design —
            never the deploy role. */}
          <dd>
            <code>{result.diagnosticsPrincipal}</code>
          </dd>
          <dt>Account</dt>
          <dd>{result.account.accountKey}</dd>
        </dl>
        <p className="muted">
          Both values go into the access template, which is on this account&rsquo;s onboarding
          screen with the external ID already filled in — so the usual path is to go there rather
          than to copy this by hand.
        </p>
        <div className="actions">
          {/*
            The usual path, so it is the primary action (IM-181). It said "go
            there" and offered no way to — the new account's onboarding screen
            was reachable only by dismissing this and finding its name in the
            list. Below the external ID, never instead of it: leaving is the
            moment the value is gone, and the warning above is still true.
            Registering is `account:onboard`, which only a role that also holds
            `account:read` has, so AccountLink always renders a link here.
          */}
          <AccountLink
            tenantId={tenantId}
            accountKey={result.account.accountKey}
            role={role}
            onOpen={onOpen}
            className="button-link primary"
          >
            Continue to onboarding <span aria-hidden="true">→</span>
          </AccountLink>
          {/* Dismissal is what refreshes the list — see `submit`. */}
          <button
            type="button"
            className="ghost"
            onClick={() => {
              done()
            }}
          >
            Done
          </button>
        </div>
      </section>
    ),
  },
  azure: {
    name: 'Azure',
    radioId: 'cloudAzure',
    accountNoun: 'subscription',
    fields: [
      {
        kind: 'text',
        name: 'subscriptionId',
        label: 'Subscription id',
        hint: <>A GUID, from the subscription&rsquo;s Overview in the Azure portal.</>,
      },
      {
        kind: 'text',
        name: 'azureTenantId',
        label: 'Tenant id',
        hint: (
          <>
            The customer&rsquo;s Microsoft Entra tenant that the subscription belongs to. Also a
            GUID, and never the same one.
          </>
        ),
      },
      {
        kind: 'choice',
        name: 'delegationScope',
        legend: 'What does the customer delegate?',
        hint: (
          <>
            Qrvey&rsquo;s read access can query every log workspace in what is delegated, so a
            shared subscription is delegated one resource group at a time.
          </>
        ),
        options: [
          {
            value: 'subscription',
            id: 'scopeSubscription',
            label: <>The whole subscription &mdash; it is dedicated to Qrvey</>,
          },
          {
            value: 'resource-groups',
            id: 'scopeResourceGroups',
            label: (
              <>Each instance&rsquo;s resource group &mdash; the subscription holds other things</>
            ),
          },
        ],
      },
    ],
    note: 'Nothing is shown once: the delegation template the next screen offers carries everything the customer needs.',
    /*
     * Azure: nothing was minted to copy, so this is not an alert and leaving
     * loses nothing. The delegation template is the next step; the account's
     * own onboarding screen offers it too.
     */
    Registered: ({ result, baseUrl, tenantId, done }) => (
      <section className="panel">
        <h3>Registered {result.account.label}</h3>
        <dl className="detail">
          <dt>Subscription</dt>
          <dd>
            <code>{result.account.subscriptionId}</code>
          </dd>
          <dt>Delegated</dt>
          <dd>
            {result.account.delegationScope === 'subscription'
              ? 'The whole subscription, which is dedicated to Qrvey'
              : 'Each instance’s resource group, one at a time'}
          </dd>
          <dt>Qrvey principal</dt>
          {/* What the delegation's one authorization names: Reader, nothing
            else. */}
          <dd>
            <code>{result.diagnosticsPrincipal}</code>
          </dd>
        </dl>
        <p className="muted">
          Next, the customer deploys the delegation template in their own tenant. It grants Qrvey
          built-in Reader and nothing else, and carries a mark made for this registration, so Qrvey
          acts only on a delegation this template made. There is nothing to copy by hand.
          {result.account.delegationScope === 'subscription'
            ? ' Deploy it once, against this subscription.'
            : ' Deploy it once for the runner’s resource group, and once per instance before that instance’s first run, each time naming the group: it creates the group and delegates only that group.'}
        </p>
        <TemplateButton
          label="Download the delegation template"
          filename={`qrvey-delegation-${result.account.delegationScope ?? 'subscription'}.json`}
          fetch={() => fetchBootstrapTemplate(baseUrl, tenantId, result.account.accountKey)}
        />
        <div className="actions">
          <button
            type="button"
            className="primary"
            onClick={() => {
              done()
            }}
          >
            Done
          </button>
        </div>
      </section>
    ),
  },
}

const Field = ({
  field,
  value,
  onChange,
}: {
  readonly field: FieldSpec
  readonly value: string | undefined
  readonly onChange: (value: string) => void
}): JSX.Element =>
  field.kind === 'text' ? (
    <div className="field">
      <label htmlFor={field.name}>{field.label}</label>
      <input
        id={field.name}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        {...(field.inputMode === undefined ? {} : { inputMode: field.inputMode })}
        aria-describedby={`${field.name}-hint`}
      />
      <span id={`${field.name}-hint`} className="hint">
        {field.hint}
      </span>
    </div>
  ) : (
    <fieldset className="field kind-choice" aria-describedby={`${field.name}-hint`}>
      <legend>{field.legend}</legend>
      {field.options.map((option) => (
        <label key={option.id} htmlFor={option.id}>
          <input
            id={option.id}
            type="radio"
            name={field.name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />{' '}
          {option.label}
        </label>
      ))}
      <span id={`${field.name}-hint`} className="hint">
        {field.hint}
      </span>
    </fieldset>
  )
